import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Edit,
  History,
  Loader2,
  Sparkles,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PromptWithVersions, Transcript } from "@/types/prompt";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function PromptDetail() {
  const { id } = useParams<{ id: string }>();
  const [prompt, setPrompt] = useState<PromptWithVersions | null>(null);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("prompt");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchPromptDetails(id);
    }
  }, [id]);

  const fetchPromptDetails = async (promptId: string) => {
    try {
      setLoading(true);

      // Fetch the prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .select("*")
        .eq("id", promptId)
        .single();

      if (promptError) throw promptError;

      // Fetch all versions for this prompt
      const { data: versionsData, error: versionsError } = await supabase
        .from("prompt_versions")
        .select("*")
        .eq("prompt_id", promptId)
        .order("version_number", { ascending: false });

      if (versionsError) throw versionsError;

      const promptWithVersions: PromptWithVersions = {
        ...promptData,
        versions: versionsData || [],
        latest_version:
          versionsData && versionsData.length > 0 ? versionsData[0] : undefined,
      };

      setPrompt(promptWithVersions);

      // If there's a transcript ID, fetch the transcript
      if (promptData.transcript_id) {
        const { data: transcriptData, error: transcriptError } = await supabase
          .from("transcripts")
          .select("*")
          .eq("id", promptData.transcript_id)
          .single();

        if (transcriptError) throw transcriptError;
        setTranscript(transcriptData);
      }
    } catch (error) {
      console.error("Error fetching prompt details:", error);
      toast({
        title: "Error",
        description: "Failed to load prompt details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (prompt?.latest_version?.content) {
      navigator.clipboard.writeText(prompt.latest_version.content);
      toast({
        title: "Copied",
        description: "Prompt copied to clipboard.",
      });
    }
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Loading prompt details...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!prompt) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">Prompt not found.</p>
            <Button className="mt-4" onClick={() => navigate("/prompt-engine")}>
              Back to Prompts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate("/prompt-engine")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">{prompt.title}</CardTitle>
            <CardDescription>
              {prompt.description || "No description provided"}
            </CardDescription>
            <div className="flex items-center mt-2 text-sm text-muted-foreground">
              <span>
                Created: {new Date(prompt.created_at).toLocaleDateString()}
              </span>
              {prompt.latest_version && (
                <span className="ml-4">
                  Latest Version: {prompt.latest_version.version_number}
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/prompt-engine/history/${prompt.id}`)}
            >
              <History className="h-4 w-4 mr-1" /> History
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/prompt-engine/edit/${prompt.id}`)}
            >
              <Edit className="h-4 w-4 mr-1" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/prompt-engine/ai-edit/${prompt.id}`)}
            >
              <Sparkles className="h-4 w-4 mr-1" /> Edit with AI
            </Button>
            <Button variant="outline" size="sm" onClick={copyToClipboard}>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="prompt">Prompt</TabsTrigger>
            <TabsTrigger value="transcript" disabled={!transcript}>
              Original Transcript
            </TabsTrigger>
          </TabsList>
          <TabsContent value="prompt" className="mt-4">
            {prompt.latest_version ? (
              <ScrollArea className="border rounded-md p-4 h-[400px]">
                <pre className="whitespace-pre-wrap font-mono text-sm">
                  {prompt.latest_version.content}
                </pre>
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-[200px] border rounded-md">
                <p className="text-muted-foreground">
                  No prompt content available.
                </p>
              </div>
            )}
          </TabsContent>
          <TabsContent value="transcript" className="mt-4">
            {transcript ? (
              <ScrollArea className="border rounded-md p-4 h-[400px]">
                <pre className="whitespace-pre-wrap font-mono text-sm">
                  {transcript.content}
                </pre>
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-[200px] border rounded-md">
                <p className="text-muted-foreground">
                  No transcript associated with this prompt.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button variant="outline" onClick={() => navigate("/prompt-engine")}>
          Back to Prompts
        </Button>
      </CardFooter>
    </Card>
  );
}
